// src/components/ClinicContext.tsx
import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import { message } from 'antd';
import { getFirestore, collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { resolveAssistantDoctorAssignment } from '../utils/resolveAssistantDoctorAssignment';

interface Clinic {
  _id: string;
  name: string;
  clinic_id?: string;
  address?: string;
  phone?: string;
  /** Optional: ID of the parent center clinic. Present only for branch clinics. */
  parent_clinic_id?: string | null;
  /** Optional: when true (on a center clinic), patient files are shared across all branches. */
  sharedPatientFile?: boolean;
}

interface ClinicContextType {
  clinics: Clinic[];
  selectedClinicId: string | null;
  selectedClinic: Clinic | null;
  setSelectedClinicId: (clinicId: string) => void;
  fetchClinics: () => Promise<Clinic[]>;
  loading: boolean;
  /**
   * When non-null, the selected clinic has shared patient file enabled.
   * Contains the center clinic_id plus all branch clinic_ids that should be queried together.
   * When null, use normal single-doctor behavior (no shared scope).
   */
  clinicScopeIds: string[] | null;
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

export const ClinicProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(
    localStorage.getItem('clinicId')
  );
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // null = no shared scope (single-doctor mode); string[] = IDs to query together
  const [clinicScopeIds, setClinicScopeIds] = useState<string[] | null>(null);

  // Fetch clinics for the logged in user (doctor or assistant)
  const fetchClinics = async (): Promise<Clinic[]> => {
    try {
      setLoading(true);
      const doctorId = localStorage.getItem('doctorId');
      const assistantId = localStorage.getItem('assistantId'); // Get assistant_id (Firebase UID)
      
      if (!doctorId && !assistantId) {
        console.log('Neither doctorId nor assistantId found - waiting for login to complete');
        return [];
      }
      
      const db = getFirestore();
      const clinicsList: Clinic[] = [];
      
      // Single source of truth: DCA collection.
      // Both owner (assistant_id = ownerUID) and assistant (assistant_id = assistantUID)
      // accounts resolve their clinic list through DCA rows.
      // IMPORTANT: Use assistantId (Firebase UID), not doctorId
      if (assistantId) {
        const doctorClinicAssistantRef = collection(db, 'doctor_clinic_assistant');
        // Try querying by assistant_id field (trimmed and with trailing space for compatibility)
        let assistantAssignmentsQuery = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId));
        let assistantAssignmentsSnapshot = await getDocs(assistantAssignmentsQuery);
        
        // If not found, try with trailing space
        if (assistantAssignmentsSnapshot.empty) {
          assistantAssignmentsQuery = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId + ' '));
          assistantAssignmentsSnapshot = await getDocs(assistantAssignmentsQuery);
        }
        
        // If still not found, try getting document by ID
        if (assistantAssignmentsSnapshot.empty) {
          const assistantDocRef = doc(db, 'doctor_clinic_assistant', assistantId);
          const assistantDocSnap = await getDoc(assistantDocRef);
          if (assistantDocSnap.exists()) {
            // Create a mock snapshot-like structure
            assistantAssignmentsSnapshot = {
              empty: false,
              size: 1,
              docs: [assistantDocSnap]
            } as any;
          }
        }
        
        // If we found assistant assignments
        if (!assistantAssignmentsSnapshot.empty) {
          console.log(`Found ${assistantAssignmentsSnapshot.size} clinic assignments for assistant ${assistantId}`);
          
          // Get all clinic IDs assigned to this assistant
          const clinicIds: string[] = [];
          
          assistantAssignmentsSnapshot.forEach((docSnap) => {
            const clinicId = docSnap.data().clinic_id;
            if (clinicId && !clinicIds.includes(clinicId)) {
              clinicIds.push(clinicId);
            }
          });
          
          console.log('Assistant is assigned to these clinics:', clinicIds);
          
          // Fetch all clinic docs in parallel instead of sequentially
          const newClinicIds = clinicIds.filter(id => !clinicsList.some(c => c._id === id));
          const clinicSnaps = await Promise.all(
            newClinicIds.map(id => getDoc(doc(db, 'clinics', id)))
          );
          for (const clinicDocSnap of clinicSnaps) {
            if (clinicDocSnap.exists()) {
              clinicsList.push({
                _id: clinicDocSnap.id,
                name: clinicDocSnap.data().clinic_name_ar || clinicDocSnap.data().clinic_name_en || clinicDocSnap.data().name || clinicDocSnap.data().location_ar || 'Unnamed Clinic',
                address: clinicDocSnap.data().address,
                phone: clinicDocSnap.data().phone,
                parent_clinic_id: clinicDocSnap.data().parent_clinic_id || null,
                sharedPatientFile: !!clinicDocSnap.data().sharedPatientFile
              });
            }
          }
        }
      }
      
      setClinics(clinicsList);
      
      // Update selected clinic: use persisted ID so refresh keeps the same clinic
      const persistedClinicId = localStorage.getItem('clinicId');
      if (clinicsList.length > 0) {
        if (persistedClinicId === '__all_clinics__' && clinicsList.length > 1) {
          // Restore "All Clinics" selection — no single clinic to highlight
          setSelectedClinicId('__all_clinics__');
          setSelectedClinic(null);
        } else if (persistedClinicId) {
          const clinic = clinicsList.find(c => c._id === persistedClinicId);
          if (clinic) {
            setSelectedClinicId(persistedClinicId);
            setSelectedClinic(clinic);
          } else {
            setSelectedClinicId(clinicsList[0]._id);
            localStorage.setItem('clinicId', clinicsList[0]._id);
            setSelectedClinic(clinicsList[0]);
          }
        } else {
          setSelectedClinicId(clinicsList[0]._id);
          localStorage.setItem('clinicId', clinicsList[0]._id);
          setSelectedClinic(clinicsList[0]);
        }
      } else {
        setSelectedClinic(null);
      }
      
      return clinicsList;
    } catch (error) {
      console.error('Error fetching clinics:', error);
      message.error('Failed to load clinics');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Update selected clinic when selectedClinicId changes
  useEffect(() => {
    if (selectedClinicId === '__all_clinics__') {
      // No single clinic to highlight — the patient list will use the full scope
      setSelectedClinic(null);
      return;
    }
    if (selectedClinicId && clinics.length > 0) {
      const clinic = clinics.find(c => c._id === selectedClinicId);
      if (clinic) {
        setSelectedClinic(clinic);
      }
    } else {
      setSelectedClinic(null);
    }
  }, [selectedClinicId, clinics]);

  /**
   * FIX: When an assistant selects a clinic, resolve the correct doctor_id from
   * doctor_clinic_assistant. Assistants can work with multiple doctors at the same
   * or different clinics - login only stored the first one. Updating here ensures
   * the API receives the correct doctor_id for the selected clinic.
   */
  useEffect(() => {
    const assistantId = localStorage.getItem('assistantId');
    // Skip doctor resolution for 'All Clinics' — login already stored the correct doctorId
    if (!assistantId || !selectedClinicId || selectedClinicId === '__all_clinics__') return;

    const resolveDoctorForClinic = async () => {
      try {
        const db = getFirestore();
        const doctorClinicAssistantRef = collection(db, 'doctor_clinic_assistant');

        // Clinics to match: selected clinic + parent (if branch with shared file)
        const selectedData = clinics.find(c => c._id === selectedClinicId);
        const parentId = selectedData?.parent_clinic_id || null;
        const clinicIdsToMatch = [selectedClinicId];
        if (parentId) clinicIdsToMatch.push(parentId);

        // Query all assignments for this assistant
        let q = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId));
        let snapshot = await getDocs(q);
        if (snapshot.empty) {
          q = query(doctorClinicAssistantRef, where('assistant_id', '==', assistantId + ' '));
          snapshot = await getDocs(q);
        }

        if (!snapshot.empty) {
          const matchingDocs = snapshot.docs.filter(d => clinicIdsToMatch.includes(d.data().clinic_id));
          const candidates = matchingDocs.length > 0 ? matchingDocs : snapshot.docs;

          let doctorId: string | null = null;
          const resolved = await resolveAssistantDoctorAssignment(
            db,
            candidates,
            assistantId,
            selectedClinicId
          );
          if (resolved) {
            doctorId = resolved.doctorId;
          }

          if (doctorId) {
            const currentStored = localStorage.getItem('doctorId');
            if (currentStored !== doctorId) {
              localStorage.setItem('doctorId', doctorId);
              window.dispatchEvent(new Event('doctorIdChanged'));
              console.log('Updated doctorId for selected clinic (prefer owner):', doctorId);
            }
          }
        }
      } catch (err) {
        console.error('Error resolving doctor for clinic:', err);
      }
    };

    resolveDoctorForClinic();
  }, [selectedClinicId, clinics]);

  /**
   * Compute clinicScopeIds whenever the selected clinic changes.
   * - '__all_clinics__' → all clinic IDs the user has access to
   * - Standalone clinic → [centerId]
   * - Center clinic → [centerId, ...branchIds]
   * - Branch clinic → [centerId, ...branchIds]
   *
   * Per-clinic sharing is always on: every doctor in the same clinic (or center+branches)
   * sees the same patient file, visits, packages, reports, etc. The legacy
   * `sharedPatientFile` Firestore flag is no longer required.
   */
  useEffect(() => {
    if (!selectedClinicId) {
      setClinicScopeIds(null);
      return;
    }

    // "All Clinics" mode: use every clinic the user has access to
    if (selectedClinicId === '__all_clinics__') {
      const allIds = clinics.map(c => c._id);
      setClinicScopeIds(allIds.length > 0 ? allIds : null);
      return;
    }

    const resolveScope = async () => {
      try {
        const db = getFirestore();
        const clinicsRef = collection(db, 'clinics');

        const selectedData = clinics.find(c => c._id === selectedClinicId);
        const parentId = selectedData?.parent_clinic_id || null;
        const centerId = parentId || (selectedClinicId as string);

        // Always include all branches of the center, regardless of sharedPatientFile flag.
        let branchIds: string[] = [];
        try {
          const branchesSnap = await getDocs(query(clinicsRef, where('parent_clinic_id', '==', centerId)));
          branchIds = branchesSnap.docs.map(d => d.id);
        } catch (_err) {
          branchIds = [];
        }

        const scopeIds = [centerId, ...branchIds.filter(id => id !== centerId)];
        setClinicScopeIds(scopeIds);
      } catch (err) {
        console.error('Error resolving clinic scope:', err);
        // Fall back to at least the selected clinic so cross-doctor sharing still works.
        setClinicScopeIds(selectedClinicId ? [selectedClinicId] : null);
      }
    };

    resolveScope();
  }, [selectedClinicId, clinics]);

  // Tracks the account key used for the last clinic fetch.
  // Key = assistantId (if present, since clinic access is based on assignment) or doctorId.
  // This prevents a duplicate fetch when resolveDoctorForClinic updates doctorId and
  // re-dispatches doctorIdChanged — the assistant hasn't changed, so clinics are the same.
  const lastFetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const checkAndFetch = () => {
      const doctorId = localStorage.getItem('doctorId');
      const assistantId = localStorage.getItem('assistantId');
      if (!doctorId && !assistantId) return;

      // Use assistantId as the stable key for assistant accounts;
      // fall back to doctorId for pure-doctor accounts.
      const key = assistantId || doctorId;
      if (key === lastFetchedKeyRef.current) return; // same account, no need to re-fetch
      lastFetchedKeyRef.current = key;

      console.log('Fetching clinics for account key:', key);
      fetchClinics();
    };

    checkAndFetch(); // initial mount
    window.addEventListener('doctorIdChanged', checkAndFetch);

    return () => {
      window.removeEventListener('doctorIdChanged', checkAndFetch);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep OwnerContext (which intentionally sits above ClinicProvider) in sync
  // for both initial automatic selection and later manual clinic switches.
  useEffect(() => {
    if (!selectedClinicId) return;
    localStorage.setItem('clinicId', selectedClinicId);
    window.dispatchEvent(new Event('clinicIdChanged'));
  }, [selectedClinicId]);

  // Wrapper used by consumers; persistence/event propagation is centralized above.
  const setSelectedClinicIdAndPersist = (clinicId: string) => {
    setSelectedClinicId(clinicId);
  };

  const value = {
    clinics,
    selectedClinicId,
    selectedClinic,
    setSelectedClinicId: setSelectedClinicIdAndPersist,
    fetchClinics,
    loading,
    clinicScopeIds
  };

  return (
    <ClinicContext.Provider value={value}>
      {children}
    </ClinicContext.Provider>
  );
};

// Custom hook for using the Clinic context
export const useClinicContext = () => {
  const context = useContext(ClinicContext);
  if (context === undefined) {
    throw new Error('useClinicContext must be used within a ClinicProvider');
  }
  return context;
}
